"use client";
// Build canonical Cloudflare R2 URL for a media reference.
// Accepts objects with `publicId`/`url`/`fileName`/`name`, or string IDs/URLs.
// If the reference already contains a `shared_media/...` path (publicId or URL),
// preserve the subpath (folders). Otherwise fall back to basename.
import { getMediaUrl } from "@/lib/utils";
import BrandsSection from "@/components/ar-IQ/BrandsSection";
import CounterAnimation from "@/components/ar-IQ/CounterAnimation";
import Footer from "@/components/ar-IQ/Footer";
import Navbar from "@/components/ar-IQ/Navbar";
import { useLanguage } from "@/contexts/LanguageContext";
import { getAbout } from "@/lib/api";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white" dir="rtl">
      {/* Preload removed: Next/Image handles preloading for `priority` images */}
      <Navbar />
      <HeroSection  />
      <AboutIntroSection />
      <StorySection />
      <VisionSection />
      <StatsSection />
      <BrandsSection />
      <Footer />
    </div>
  );
};

const HeroSection = () => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [floatingParticles, setFloatingParticles] = useState([]);
  const [isMounted, setIsMounted] = useState(false);
  const [heroData, setHeroData] = useState({
    title: "",
    titleHighlight: "",
    subtitle: "",
    video: null,
  });
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();

  // Always call useScroll - this is safe now
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.2]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);

  useEffect(() => {
    setIsMounted(true);
    // Generate particles
    const particles = Array.from({ length: 25 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 6 + Math.random() * 4,
    }));
    setFloatingParticles(particles);

    // Fetch hero data
    const fetchHeroData = async () => {
      try {
        setLoading(true);
        const data = await getAbout(lang);
        if (data?.hero) {
          setHeroData({
            title: data.hero.title || "",
            titleHighlight: data.hero.titleHighlight || "",
            subtitle: data.hero.subtitle || "",
            video: data.hero.video,
          });
        }
      } catch (error) {
        console.error("Failed to fetch hero data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHeroData();
  }, [lang]);

  const videoUrl = heroData.video?.url || getMediaUrl(heroData.video) || getMediaUrl("hero.mp4");
  const hasVideoLoaded = heroData.video?.url;

  return (
    <motion.section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ y: heroY, opacity: heroOpacity }}
    >
      {/* Video Background */}
      <div className="absolute inset-0 will-change-transform">
        {!hasVideoLoaded && loading ? (
          // Show poster/placeholder while loading
          <div 
            className="w-full h-full bg-cover bg-center"
            style={{ backgroundImage: "url('/arabic.jpg')" }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50"></div>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster={getMediaUrl("arabic.jpg")}
              className="w-full h-full object-cover"
              key={videoUrl} // Force re-render when URL changes
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50"></div>
          </>
        )}
      </div>

      {/* Loading Overlay - Only show while fetching data */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#0a367b]/50 backdrop-blur-sm">
          <div className="text-center text-white">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#dfa428] border-t-transparent mx-auto mb-4"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 bg-[#dfa428] rounded-full animate-pulse"></div>
              </div>
            </div>
            <p className="text-xl font-semibold">جاري التحميل...</p>
          </div>
        </div>
      )}

      {/* Floating Particles */}
      {isMounted && (
        <div className="absolute inset-0">
          {floatingParticles.map((particle) => (
            <motion.div
              key={particle.id}
              className="absolute w-2 h-2 bg-[#dfa428]/40 rounded-full"
              style={{
                top: `${particle.top}%`,
                left: `${particle.left}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
                scale: [0, 1.5, 0],
              }}
              transition={{
                duration: particle.duration,
                repeat: Number.POSITIVE_INFINITY,
                delay: particle.delay,
              }}
            />
          ))}
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: loading ? 0 : 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="max-w-5xl mx-auto"
        >
          <motion.h1
            className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: loading ? 0 : 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 1 }}
          >
            {heroData.title || "نحن شركة إبداعية وإعلانية"}
            {(heroData.titleHighlight || " متكاملة الخدمات") && (
              <>
                <br />
                <span className="text-[#dfa428]">{heroData.titleHighlight || " متكاملة الخدمات"}</span>
              </>
            )}
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl leading-relaxed opacity-90 mb-12"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: loading ? 0 : 1, y: 0 }}
            transition={{ duration: 1, delay: 1.5 }}
          >
            {heroData.subtitle ||
              "نعمل على مستوى العالم مع أكبر العلامات التجارية أطلق العنان لإمكانيات علامتك التجارية من خلال الإعلانات المؤثرة والتصميمات الاستثنائية"}
          </motion.p>
        </motion.div>
      </div>

      {/* Animated Shapes */}
      <motion.div
        className="absolute top-20 right-20 w-32 h-32 bg-[#dfa428]/20 rounded-full"
        animate={{
          scale: [1, 1.3, 1],
          rotate: [0, 180, 360],
        }}
        transition={{ duration: 12, repeat: Number.POSITIVE_INFINITY }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-24 h-24 bg-white/10 rounded-full"
        animate={{
          scale: [1.2, 1, 1.2],
          rotate: [360, 180, 0],
        }}
        transition={{ duration: 8, repeat: Number.POSITIVE_INFINITY }}
      />
      <motion.div
        className="absolute top-1/3 left-1/4 w-16 h-16 bg-[#dfa428]/30 transform rotate-45"
        animate={{
          rotate: [45, 225, 405],
          scale: [1, 1.4, 1],
        }}
        transition={{ duration: 6, repeat: Number.POSITIVE_INFINITY }}
      />
    </motion.section>
  );
};
const AboutIntroSection = () => {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const textRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [introData, setIntroData] = useState({
    title: "",
    subtitle: "",
    image: null,
  });
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const imageY = useTransform(scrollYProgress, [0, 1], ["20%", "-20%"]);
  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.2, 1, 0.8]);
  const textX = useTransform(scrollYProgress, [0, 0.5], ["100px", "0px"]);

  const isImageInView = useInView(imageRef, { once: true, margin: "-100px" });
  const isTextInView = useInView(textRef, { once: true, margin: "-100px" });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    const fetchIntroData = async () => {
      try {
        setLoading(true);
        const data = await getAbout(lang);
        if (data?.aboutIntro) {
          setIntroData({
            title: data.aboutIntro.title || "",
            subtitle: data.aboutIntro.subtitle || "",
            image: data.aboutIntro.image,
          });
        }
      } catch (error) {
        console.error("Failed to fetch intro data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchIntroData();

    return () => window.removeEventListener("resize", checkMobile);
  }, [lang]);

  const imageUrl = introData.image?.url || getMediaUrl(introData.image) || getMediaUrl("acctern.png");

  return (
    <section ref={sectionRef} className="py-20 bg-gray-50 overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            ref={imageRef}
            className="relative order-2 lg:order-1"
            style={isMobile ? {} : { y: imageY }}
          >
            <motion.div
              className="relative overflow-hidden rounded-3xl shadow-2xl"
              style={isMobile ? {} : { scale: imageScale }}
              initial={{ opacity: 0, x: -100 }}
              animate={isImageInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 1.2, ease: "easeOut" }}
            >
              <Image
                src={imageUrl}
                alt="قصتنا"
                width={600}
                height={500}
                className="w-full h-[500px] object-cover"
                priority
                loading="eager"
                fetchPriority="high"
                quality={90}
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0a367b]/20 to-transparent"></div>

              <motion.div
                className="absolute top-8 right-8 bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg"
                animate={isMobile ? {} : { y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY }}
              >
                <div className="text-2xl font-bold text-[#0a367b]">قصتنا</div>
                <div className="text-sm text-gray-600">منذ البداية</div>
              </motion.div>

              <motion.div
                className="absolute bottom-8 left-8 bg-[#dfa428]/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg"
                animate={isMobile ? {} : { y: [0, 10, 0] }}
                transition={{
                  duration: 3,
                  repeat: Number.POSITIVE_INFINITY,
                  delay: 1.5,
                }}
              >
                <div className="text-2xl font-bold text-[#0a367b]">2012</div>
                <div className="text-sm text-[#0a367b]">التأسيس</div>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div
            ref={textRef}
            className="space-y-8 order-1 lg:order-2"
            style={isMobile ? {} : { x: textX }}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={isTextInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-[#0a367b] mb-6 leading-tight">
                {loading
                  ? "جاري التحميل..."
                  : introData.title || "قصتنا منذ عام 2012"}
              </h2>
            </motion.div>

            <motion.div
              className="space-y-6 text-lg text-gray-700 leading-relaxed"
              initial={{ opacity: 0, y: 50 }}
              animate={isTextInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {loading ? (
                <p>جاري التحميل...</p>
              ) : (
                <p>
                  {introData.subtitle ||
                    "تأسست شركة نخلة بابل سنة 2012 من قبل مجموعة من الشباب العراقيين لديهم الرغبة في تنفيذ ما يملكون من خبرات ومهارات في وطنهم, حيث لديهم خبرة طويلة في مجال التسويق والاعلان الخاص بالشركات والمؤسسات العالمية الكبرى في مختلف الصناعات والقطّاعات."}
                </p>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={isTextInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Link href="/ar-IQ/services">
                <motion.button
                  className="bg-[#0a367b] text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-[#0a367b]/90 transition-all duration-300 shadow-lg"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  عرض الخدمات
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const StorySection = () => {
  const sectionRef = useRef(null);
  const [particles, setParticles] = useState([]);
  const [isMounted, setIsMounted] = useState(false);
  const [storyData, setStoryData] = useState({
    title: "",
    subtitle: "",
    images: [],
  });
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);

  const isInView = useInView(sectionRef, { once: true });

  useEffect(() => {
    setIsMounted(true);
    const generatedParticles = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 4 + Math.random() * 2,
    }));
    setParticles(generatedParticles);

    const fetchStoryData = async () => {
      try {
        setLoading(true);
        const data = await getAbout(lang);
        if (data?.story) {
          setStoryData({
            title: data.story.title || "",
            subtitle: data.story.subtitle || "",
            images: data.story.images || [],
          });
        }
      } catch (error) {
        console.error("Failed to fetch story data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStoryData();
  }, [lang]);

  const defaultImages = [
    getMediaUrl("DSC07858.jpg"),
    getMediaUrl("amuaj.jpg"),
    getMediaUrl("talabat.jpg"),
    getMediaUrl("acctern.png"),
  ];

  const displayImages =
    storyData.images?.length > 0
      ? storyData.images.map((img) => (img?.url ? img.url : getMediaUrl(img))).slice(0, 4)
      : defaultImages;

  return (
    <section
      ref={sectionRef}
      className="relative py-32 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0C0927 0%, #233C78 100%)",
      }}
    >
      <motion.div className="absolute inset-0" style={{ y: backgroundY }}>
        {isMounted && particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute w-3 h-3 bg-[#dfa428]/30 rounded-full"
            style={{
              top: `${particle.top}%`,
              left: `${particle.left}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 1, 0],
              scale: [0, 1.2, 0],
            }}
            transition={{
              duration: particle.duration,
              repeat: Number.POSITIVE_INFINITY,
              delay: particle.delay,
            }}
          />
        ))}

        <motion.div
          className="absolute top-20 right-20 w-48 h-48 bg-[#dfa428]/10 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ duration: 15, repeat: Number.POSITIVE_INFINITY }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-36 h-36 bg-white/5 rounded-full"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{ duration: 12, repeat: Number.POSITIVE_INFINITY }}
        />
      </motion.div>

      <motion.div
        className="relative z-10 container mx-auto px-6"
        style={{ y: contentY }}
      >
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            className="grid grid-cols-2 gap-4"
            initial={{ opacity: 0, x: -100 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1.2 }}
          >
            {displayImages.slice(0, 4).map((imageSrc, index) => (
              <motion.div
                key={index}
                className="relative overflow-hidden rounded-2xl shadow-xl"
                whileHover={{ scale: 1.05, rotate: index % 2 === 0 ? 2 : -2 }}
                transition={{ duration: 0.3 }}
              >
                <Image
                  src={imageSrc || "/placeholder.svg"}
                  alt={`Story ${index + 1}`}
                  width={300}
                  height={192}
                  className="w-full h-48 object-cover"
                  loading={index < 2 ? "eager" : "lazy"}
                  fetchPriority={index < 2 ? "high" : "auto"}
                  quality={85}
                />
                {index % 2 === 1 && (
                  <div className="absolute inset-0 bg-gradient-to-t from-[#dfa428]/30 to-transparent"></div>
                )}
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="text-white space-y-8"
            initial={{ opacity: 0, x: 100 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1.2, delay: 0.3 }}
          >
            <motion.h2
              className="text-5xl font-bold leading-tight"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              {loading
                ? "جاري التحميل..."
                : storyData.title || "رحلة من الإبداع والتميز"}
            </motion.h2>

            <motion.p
              className="text-xl leading-relaxed opacity-90"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              {loading
                ? "جاري التحميل..."
                : storyData.subtitle ||
                  "منذ تأسيسنا، نسعى لتقديم حلول إعلانية مبتكرة تلبي احتياجات عملائنا وتحقق أهدافهم التسويقية بأعلى معايير الجودة والاحترافية."}
            </motion.p>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

const VisionSection = () => {
  const sectionRef = useRef(null);
  const [visionData, setVisionData] = useState({
    title: "",
    subtitle: "",
  });
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();

  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  useEffect(() => {
    const fetchVisionData = async () => {
      try {
        setLoading(true);
        const data = await getAbout(lang);
        if (data?.vision) {
          setVisionData({
            title: data.vision.title || "",
            subtitle: data.vision.subtitle || "",
          });
        }
      } catch (error) {
        console.error("Failed to fetch vision data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVisionData();
  }, [lang]);

  return (
    <section ref={sectionRef} className="relative h-screen overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 to-[#0a367b]/90" />

      <div className="relative z-10 h-full flex items-center justify-center">
        <div className="text-center text-white max-w-5xl mx-auto px-6">
          <motion.h2
            className="text-6xl md:text-8xl font-bold mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1 }}
          >
            {loading
              ? "جاري التحميل..."
              : visionData.title || "رؤيتنا للمستقبل"}
          </motion.h2>

          <motion.p
            className="text-2xl md:text-3xl opacity-90 leading-relaxed mb-12"
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {loading
              ? "جاري التحميل..."
              : visionData.subtitle ||
                "نسعى لتكون نخلة بابل الخيار الأول في العراق والمنطقة للحلول الإعلانية المبتكرة والتقنيات المتطورة"}
          </motion.p>

          <div className="flex justify-center space-x-6 space-x-reverse">
            <Link href="/ar-IQ/careers">
              <button className="yellow-button bg-[#dfa428] text-[#0a367b] px-10 py-5 rounded-full text-xl font-bold hover:bg-yellow-500 transition-all duration-300 shadow-lg">
                انضم إلينا
              </button>
            </Link>
            <Link href="/ar-IQ/contact">
              <button className="border-3 border-white mr-4 text-white px-10 py-5 rounded-full text-xl font-bold hover:bg-white hover:text-[#0a367b] transition-all duration-300">
                تواصل معنا
              </button>
            </Link>
          </div>
        </div>
      </div>

      <motion.div
        className="absolute top-20 right-20 w-24 h-24 bg-[#dfa428]/30 rounded-full"
        animate={{
          y: [0, -30, 0],
          rotate: [0, 180, 360],
          scale: [1, 1.2, 1],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-20 h-20 bg-white/20 rounded-full"
        animate={{
          y: [0, 30, 0],
          rotate: [360, 180, 0],
          scale: [1.2, 1, 1.2],
        }}
        transition={{ duration: 6, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 left-1/4 w-16 h-16 bg-[#dfa428]/40 transform rotate-45"
        animate={{
          rotate: [45, 225, 45],
          scale: [1, 1.5, 1],
          x: [0, 20, 0],
        }}
        transition={{ duration: 4, repeat: Infinity }}
      />
    </section>
  );
};

const StatsSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();

  useEffect(() => {
    const fetchStatsData = async () => {
      try {
        setLoading(true);
        const data = await getAbout(lang);
        if (data?.achievements && Array.isArray(data.achievements)) {
          setAchievements(data.achievements);
        }
      } catch (error) {
        console.error("Failed to fetch stats data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatsData();
  }, [lang]);

  const defaultStats = [
    {
      number: 1500,
      label: "حملة اعلانية ناجحة",
      color: "from-[#dfa428] to-yellow-600",
    },
    {
      number: 500,
      label: "شاشات ولوحات",
      color: "from-blue-400 to-blue-600",
    },
    {
      number: 7,
      label: "وكلاء حصريين في اكبر المراكز التجارية",
      color: "from-green-400 to-green-600",
    },
    {
      number: 98,
      label: "ادراة مواقع التواصل الاجتماعي",
      color: "from-purple-400 to-purple-600",
    },
  ];

  // Helper to extract localized value
  const getLocalizedValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value !== null) {
      return value.ar || value.en || fallback;
    }
    return fallback;
  };

  const displayStats =
    achievements.length > 0
      ? achievements.map((ach, index) => ({
          number:
            typeof ach.number === "number"
              ? ach.number
              : parseInt(ach.number) || 0,
          label: getLocalizedValue(ach.label, ""),
          color: "from-[#dfa428] to-yellow-600",
        }))
      : defaultStats;

  return (
    <section
      ref={sectionRef}
      className="py-32 bg-gray-50 relative overflow-hidden"
    >
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#dfa428]/10 to-[#0a367b]/10 rounded-full"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-tr from-[#0a367b]/10 to-[#dfa428]/10 rounded-full"
          animate={{
            rotate: [360, 0],
            scale: [1.2, 1, 1.2],
          }}
          transition={{ duration: 15, repeat: Number.POSITIVE_INFINITY }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl md:text-6xl font-bold text-[#0a367b] mb-6">
            إنجازاتنا بالأرقام
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            نفتخر بما حققناه من نجاحات وإنجازات على مدار السنوات الماضية
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {displayStats.map((stat, index) => (
            <motion.div
              key={index}
              className="relative"
              initial={{ opacity: 0, y: 100 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              <motion.div
                className="bg-white rounded-3xl p-8 shadow-xl text-center relative overflow-hidden group hover:shadow-2xl transition-all duration-500"
                whileHover={{ scale: 1.05, y: -10 }}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                ></div>

                <div className="relative z-10">
                  <CounterAnimation
                    target={stat.number}
                    suffix="+"
                    duration={2}
                    className="text-4xl md:text-5xl font-bold text-[#0a367b] mb-4"
                  />
                  <p className="text-gray-600 font-medium leading-relaxed">
                    {stat.label}
                  </p>
                </div>

                <motion.div
                  className="absolute top-4 right-4 w-3 h-3 bg-[#dfa428]/60 rounded-full"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.6, 1, 0.6],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Number.POSITIVE_INFINITY,
                    delay: index * 0.3,
                  }}
                />
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutPage;
